import { useQuery } from '@tanstack/react-query';
import { agentClient } from '../../lib/connect/clients';
/**
 * Fetches chat history via shanks's AgentService Connect surface
 * (routed through sabaody under /mono.agent.v1.AgentService/ListHistory).
 *
 * Pre-cut this hook posted /api/chat to express, which merged the Mongo
 * Chat collection with shanks's own history. Post-cut, shanks is the
 * only source of truth (it writes through to pangaea) and the merge
 * happens server-side inside ListHistory. The `lastMessage` /
 * `projectName` enrichment that express performed is *not* in
 * AgentService.ListHistory's response shape today — it will land as a
 * separate proto extension (tracked in the follow-up); for now the
 * sidebar renders title + timestamp without the preview line.
 */ async function fetchChats(limit = 10000) {
    // user_id is filled by the gateway-auth principal on shanks; passing
    // an empty string lets the server pull from the JWT instead of trusting
    // a client-supplied value.
    const resp = await agentClient.listHistory({
        userId: '',
        limit,
        offset: 0
    });
    const chats = resp.conversations.map((c)=>{
        const createdAt = c.createdAt?.toDate().toISOString() ?? new Date(0).toISOString();
        const updatedAt = c.updatedAt?.toDate().toISOString() ?? createdAt;
        return {
            // Preserve the legacy `shanks-<uuid>` shape so existing bookmarks
            // and inline links keep resolving. The shanks ID space and the
            // pangaea collab.conversations row use the bare uuid; the prefix
            // is a wire-level affordance for routing in the frontend.
            chatId: `shanks-${c.id}`,
            title: c.title,
            createdAt,
            updatedAt,
            projectId: c.projectId || undefined,
            projectName: undefined,
            lastMessage: undefined
        };
    });
    return {
        chats,
        pagination: {
            total: chats.length,
            page: 1,
            limit: chats.length,
            totalPages: 1
        }
    };
}
/**
 * Hook for fetching chat history data.
 * Use this in parent components to check if there are chats
 * before rendering the toolbar/filters.
 */ export function useChatHistory() {
    const query = useQuery({
        queryKey: [
            'chats'
        ],
        queryFn: ()=>fetchChats(),
        staleTime: 30000
    });
    return {
        chats: query.data?.chats ?? [],
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch,
        hasChats: (query.data?.chats?.length ?? 0) > 0
    };
}
/**
 * The caller's `limit` most recently updated chats (SAD-1005): one bounded
 * read for a surface that shows a few, never every chat ever. ListHistory
 * answers newest-updated first. Keyed under ['chats'], so the History page's
 * invalidations refresh it too.
 */ export function useRecentChats(limit) {
    const query = useQuery({
        queryKey: [
            'chats',
            'recent',
            limit
        ],
        queryFn: ()=>fetchChats(limit),
        staleTime: 30000
    });
    return {
        chats: query.data?.chats ?? [],
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch
    };
}
